import { Page } from "../../../components/Page";
import { Card } from "../../../components/Card";
import { WithAuthorization } from "../../../context";
import { useTranslation } from "react-i18next";
import { showErrorToast, showSuccessToast } from "../../../utils";
import { Box, Button, chakra, Flex, Heading, useToast } from "@chakra-ui/react";
import { FormInput } from "../../../components/Forms";
import { NavigationTabs } from "../../../components/NavigationTabs";
import { useForm } from "react-hook-form";
import { hideAppSpinner, showAppSpinner } from "../../../context/Store/slices/appSpinnerSlice";
import { useDispatch } from "react-redux";
import { useQueryClient } from "react-query";
import { mapModelsData } from "./helpers";
import {
  multimodalTransportationKeys,
  useCreateModel,
  useDeleteModel,
  useGetModels,
  useUpdateModel
} from "../../../hooks/query";

function MultimodalTransportation() {
  const { t } = useTranslation("translation", {
    keyPrefix: "multimodalTransportation"
  });
  const { t: globalTranslation } = useTranslation("translation");

  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const toast = useToast();

  const {
    data: modelsData,
    isFetching: isFetchingModelsData,
    isLoading : isLoadingModelsData
  } = useGetModels({
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    refetchOnMount:"always"
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm({});

  const handleError = (err) => {
    let errorMessage = err.response?.data?.parameters?.message ?? err.message;
    if (err.response?.status === 409 && err.response?.data?.parameters?.resourceName === "MultimodalTransportation") {
      errorMessage = t("messages.duplicatedModelName");
    }
    showErrorToast(toast, errorMessage);
  };

  const mutationConfig = {
    onMutate: () => dispatch(showAppSpinner()),
    onSuccess: () => {
      queryClient.invalidateQueries(multimodalTransportationKeys.models());
      queryClient.invalidateQueries(multimodalTransportationKeys.transportTypes());
      reset();
    },
    onError: err => handleError(err),
    onSettled: () => dispatch(hideAppSpinner())
  };

  const { mutate: createModel } = useCreateModel(mutationConfig);
  const { mutate: updateModel } = useUpdateModel(mutationConfig);
  const { mutate: deleteModel } = useDeleteModel(mutationConfig);

  const onSubmitCreateModel = (submitData) => {
    createModel({ data: { modalName: submitData.modalName } });
  };

  const saveMethod = (modelData) => {
    if (modelData) {
      updateModel({
        id: modelData.id,
        data: modelData
      }, {
        onSuccess: () => {
          showSuccessToast(toast, t("messages.modelSaved", { modelName: modelsData?.find(el => el.id === modelData.id)?.modalName }));
          queryClient.invalidateQueries(multimodalTransportationKeys.models());
        }
      });
    }
  };

  return (
    <Page title={t("page.title")} subTitle={t("page.subTitle")}>
      <Card>
        <Box mb={10}>
          <chakra.form id="addModel" onSubmit={handleSubmit(onSubmitCreateModel)}>
            <Flex gap="4" marginBlockStart="5" alignItems="baseline">
              <FormInput
                placeholder={t("inputs.modelNamePlaceholder")}
                errors={errors}
                {...register("modalName", {
                  required: globalTranslation("entityRequired", { entityName: t("inputs.modelNamePlaceholder") })
                })}
              />
              <Button
                colorScheme="customScheme"
                width="32"
                type="submit"
                title={t("buttons.addModel")}
              >
                {t("buttons.addModel")}
              </Button>
            </Flex>
          </chakra.form>
        </Box>
        {!isFetchingModelsData && !isLoadingModelsData && modelsData && modelsData.length > 0 ?
          <NavigationTabs
            elements={mapModelsData(modelsData)}
            tabTitle={null}
            subTitleAttribute={null}
            saveMethod={saveMethod}
            formSubmission={{
              allowed: true,
              requested: false,
              decline: () => {
              }
            }}
            deleteItemEntity={{
              entityName: t("labels.transportationModel"),
              deleteItem: deleteModel,
              queryKeys: multimodalTransportationKeys.models(),
              successMessage: t("messages.modelDeleted")
            }}
            showFinaliseButton={false}
          /> :
          <Heading size="md" marginBlock="1">
            {t("labels.missingModels")}
          </Heading>
        }
      </Card>
    </Page>
  );
}

export default WithAuthorization(MultimodalTransportation);